/**
 * Done. / ChiroCPD — RouteGuard (index route "/")
 *
 * Checks auth state and redirects:
 *   - Loading → spinner
 *   - Not authenticated → LandingPage (subscribe via Stripe)
 *   - Authenticated, survey not completed → /onboarding
 *   - Authenticated, survey completed → /dashboard
 */

import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/hooks/useAuth';
import { LandingPage } from '@/pages/LandingPage';

export function RouteGuard(): React.ReactElement {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();

  useEffect(() => {
    if (loading || !user) return;
    if (!user.surveyCompleted) {
      navigate('/onboarding', { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--surface-subtle)]">
        <div className="flex flex-col items-center gap-4">
          <svg
            className="h-10 w-10 animate-spin text-[var(--color-done-500)]"
            fill="none"
            viewBox="0 0 24 24"
            aria-label="Loading…"
            role="img"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-sm text-[var(--text-secondary)]">Loading ChiroCPD…</p>
        </div>
      </div>
    );
  }

  // Not authenticated — show landing page with subscribe button
  if (!user) {
    return <LandingPage />;
  }

  // Authenticated — useEffect above handles redirect
  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--surface-subtle)]">
      <p className="text-sm text-[var(--text-secondary)]">Redirecting…</p>
    </div>
  );
}
